import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Loader2, CheckCircle2, Circle } from 'lucide-react';
import { cn } from '../lib/utils';

export default function ChecklistMode() {
    const { t } = useTranslation();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [checkedItems, setCheckedItems] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [completed, setCompleted] = useState(false);

    useEffect(() => {
        fetchTemplates();
    }, [t]); // Re-fetch when language changes

    const fetchTemplates = async () => {
        try {
            // Mock templates for demo
            const mockTemplates = [
                {
                    id: 1,
                    name: t('daily_safety_check'),
                    description: t('safety_inspection_desc'),
                    items: JSON.stringify([
                        t('check_emergency_exits'),
                        t('verify_fire_extinguishers'),
                        t('inspect_electrical_panels'),
                        t('test_emergency_lighting'),
                        t('check_first_aid_supplies')
                    ])
                },
                {
                    id: 2,
                    name: t('equipment_handover'),
                    description: t('equipment_transfer_desc'),
                    items: JSON.stringify([
                        t('equipment_clean_functional'),
                        t('all_accessories_included'),
                        t('documentation_complete'),
                        t('serial_numbers_verified'),
                        t('condition_report_signed')
                    ])
                },
                {
                    id: 3,
                    name: t('event_setup'),
                    description: t('event_setup_desc'),
                    items: JSON.stringify([
                        t('stage_setup_complete'),
                        t('sound_system_tested'),
                        t('lighting_programmed'),
                        t('safety_barriers_placed'),
                        t('emergency_procedures_briefed')
                    ])
                }
            ];
            setTemplates(mockTemplates);
        } catch (error) {
            console.error("Failed to fetch templates", error);
        } finally {
            setLoading(false);
        }
    };

    const handleStart = (template) => {
        setSelectedTemplate(template);
        setCheckedItems({});
        setCompleted(false);

        // Parse items if string
        let items = [];
        try {
            items = JSON.parse(template.items);
        } catch (e) {
            items = []; // Fallback
        }
        // Initialize checked state
        const initialChecks = {};
        items.forEach(item => initialChecks[item] = false);
        setCheckedItems(initialChecks);
    };

    const toggleItem = (item) => {
        setCheckedItems(prev => ({
            ...prev,
            [item]: !prev[item]
        }));
    };

    const handleSubmit = async () => {
        if (!selectedTemplate) return;
        setSubmitting(true);
        try {
            const response = await fetch('http://localhost:3001/api/checklists/submissions/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    template_id: selectedTemplate.id,
                    values: JSON.stringify(checkedItems),
                    status: "Completed"
                }),
            });
            
            if (!response.ok) throw new Error('Failed to submit');
            
            setCompleted(true);
            setTimeout(() => {
                setSelectedTemplate(null);
                setCompleted(false);
            }, 2000);
        } catch (error) {
            console.error("Failed to submit checklist", error);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center p-8"><Loader2 className="animate-spin text-blue-600" /></div>;
    }

    if (selectedTemplate) {
        // Active Checklist View
        const items = JSON.parse(selectedTemplate.items);

        if (completed) {
            return (
                <div className="flex flex-col items-center justify-center h-64 space-y-4">
                    <CheckCircle2 className="h-16 w-16 text-green-500" />
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('checklist_complete', 'Checklist Complete!')}</h2>
                    <p className="text-gray-500 dark:text-gray-400">{t('good_job', 'Good job.')}</p>
                </div>
            );
        }

        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold">{selectedTemplate.name}</h2>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedTemplate(null)}>{t('cancel', 'Cancel')}</Button>
                </div>

                <div className="space-y-3">
                    {items.map((item, idx) => (
                        <div
                            key={idx}
                            onClick={() => toggleItem(item)}
                            className={cn(
                                "flex items-center p-4 rounded-xl border transition-all cursor-pointer select-none",
                                checkedItems[item]
                                    ? "bg-blue-50 border-blue-200 text-blue-700"
                                    : "bg-white border-gray-200 hover:border-gray-300"
                            )}
                        >
                            {checkedItems[item] ? (
                                <CheckCircle2 className="h-6 w-6 mr-3 flex-shrink-0" />
                            ) : (
                                <Circle className="h-6 w-6 mr-3 flex-shrink-0 text-gray-300" />
                            )}
                            <span className={cn("font-medium", checkedItems[item] && "line-through opacity-70")}>
                                {item}
                            </span>
                        </div>
                    ))}
                </div>

                <Button
                    className="w-full mt-6 h-12 text-lg"
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? <Loader2 className="animate-spin mr-2" /> : t('submit_checklist', 'Submit Checklist')}
                </Button>
            </div>
        );
    }

    // Template Selection View
    return (
        <div className="space-y-4">
            <div className="text-center space-y-2 mb-6">
                <h2 className="text-2xl font-bold tracking-tight">{t('checklists', 'Checklists')}</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm">{t('select_procedure', 'Select a procedure to start.')}</p>
            </div>

            <div className="grid gap-4">
                {templates.map(template => (
                    <Card
                        key={template.id}
                        className="cursor-pointer hover:shadow-lg transition-all active:scale-95"
                        onClick={() => handleStart(template)}
                    >
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-lg font-bold">{template.name}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <CardDescription>{template.description}</CardDescription>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}